import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CategoryData, ContentLibraryData, VideoData, updateCategory, getContentLibrariesByModule, getAllContent } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { captureHandledException } from '@/lib/sentry';

interface EditCategoryModalProps {
  open: boolean;
  onClose: () => void;
  category: CategoryData | null;
  onCategoryUpdated: () => void;
}

export default function EditCategoryModal({ open, onClose, category, onCategoryUpdated }: EditCategoryModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contentLibrary, setContentLibrary] = useState('');
  const [module, setModule] = useState('');
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [contentLibraries, setContentLibraries] = useState<ContentLibraryData[]>([]);
  const [associatedVideos, setAssociatedVideos] = useState<VideoData[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (category && open) {
      logger.log('Loading category data:', category);
      setTitle(category.title || '');
      setDescription(category.description || '');
      setModule(category.module || '');
      setTag(category.tag || '');
      
      // Load libraries for the category's module first, then set content library
      if (category.module) {
        loadLibrariesForModule(category.module).then(() => {
          // Add a small delay to ensure the Select component is fully updated with options
          setTimeout(() => {
            logger.log('Setting content library after libraries loaded:', category.contentLibrary);
            setContentLibrary(category.contentLibrary || '');
          }, 100);
        });
      } else {
        setContentLibrary(category.contentLibrary || '');
      }
      
      // Load associated videos
      loadAssociatedVideos(category.id || '', category.tag || '');
      
      logger.log('Form fields set directly');
    }
  }, [category, open]);

  const loadLibrariesForModule = async (module: string) => {
    try {
      const libraries = await getContentLibrariesByModule(module);
      logger.log(`Loaded ${libraries.length} libraries for module "${module}":`, libraries);
      setContentLibraries(libraries);
      return libraries;
    } catch (error) {
      logger.error('Error loading content libraries:', error);
      setContentLibraries([]);
      return [];
    }
  };

  const loadAssociatedVideos = async (categoryId: string, categoryTag: string) => {
    setLoadingVideos(true);
    try {
      // Get all content (videos and articles) to find associated items
      const allContent = await getAllContent();
      
      // Use the same logic as the categories page: check for both categoryId and categoryTag, and only include enabled content that is not blocked
      const filtered = allContent.filter((content: VideoData) => 
        content.contentCategories && 
        content.enabled === true &&
        content.status !== 'Blocked' &&
        (content.contentCategories.includes(categoryId) || content.contentCategories.includes(categoryTag))
      );
      setAssociatedVideos(filtered);
      logger.log(`Found ${filtered.length} content items associated with category "${categoryTag}" (checking both ID: "${categoryId}" and tag: "${categoryTag}")`);
    } catch (error) {
      logger.error('Error loading associated videos:', error);
      setAssociatedVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };

  const handleModuleChange = (newModule: string) => {
    setModule(newModule);
    setContentLibrary(''); // Reset content library when module changes
    loadLibrariesForModule(newModule);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category?.id) return;

    try {
      setLoading(true);
      
      // Don't update the tag field - it's read-only
      const updateData = {
        title,
        description,
        contentLibrary,
        module,
        tag: category.tag // Keep original tag
      };

      await updateCategory(category.id, updateData);
      
      toast({
        title: "Category updated",
        description: "The category has been successfully updated.",
      });
      
      onCategoryUpdated();
      onClose();
    } catch (error: any) {
      captureHandledException(error, "category.update");
      toast({
        title: "Failed to update category",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setContentLibrary('');
      setModule('');
      setTag('');
      setContentLibraries([]);
      setAssociatedVideos([]);
    }
  }, [open]);

  if (!category) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
        </DialogHeader>
        <div className="flex gap-6 flex-1 overflow-hidden">
          {/* Form Column */}
          <div className="flex-1 min-w-0">
            <form onSubmit={handleSubmit} className="space-y-4 h-full flex flex-col">
              <div className="flex-1 space-y-4 overflow-y-auto">
                <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Category title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Category description"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="module">Module</Label>
            <Select value={module} onValueChange={handleModuleChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="mind">Mind</SelectItem>
                <SelectItem value="body">Body</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contentLibrary">Content Library</Label>
            <Select 
              value={contentLibrary} 
              onValueChange={setContentLibrary}
              key={`${module}-${contentLibraries.length}`} // Force re-render when libraries change
            >
              <SelectTrigger>
                <SelectValue placeholder="Select content library" />
              </SelectTrigger>
              <SelectContent>
                {contentLibraries.map(library => (
                  <SelectItem key={library.id} value={library.key || library.name || ''}>
                    {library.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tag">Tag (Read-only)</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="tag"
                value={tag}
                disabled
                className="bg-gray-50"
              />
              <Badge variant="outline" className="text-xs">
                Not editable
              </Badge>
            </div>
            <p className="text-xs text-gray-500">
              The tag cannot be changed as it may be referenced by existing content.
            </p>
          </div>
                </div>

              <DialogFooter className="mt-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Category'}
                </Button>
              </DialogFooter>
            </form>
          </div>

          {/* Videos Column */}
          <div className="flex-1 min-w-0 border-l pl-6">
            <div className="h-full flex flex-col">
              <h3 className="text-lg font-semibold mb-4">
                Associated Videos ({associatedVideos.length})
              </h3>
              
              {loadingVideos ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-gray-500">Loading videos...</div>
                </div>
              ) : associatedVideos.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-gray-500">No videos found with this category</div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                  {associatedVideos.map(video => (
                    <div key={video.id} className="border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start gap-3">
                        {video.thumbnailUrl && (
                          <img 
                            src={video.thumbnailUrl} 
                            alt={video.title}
                            className="w-16 h-12 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-sm line-clamp-2 mb-1">
                            {video.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="capitalize">{video.mediaType}</span>
                            <span>•</span>
                            <span className="capitalize">{video.status}</span>
                            {video.author && (
                              <>
                                <span>•</span>
                                <span>{video.author}</span>
                              </>
                            )}
                          </div>
                          {video.modules && video.modules.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {video.modules.map(moduleTag => (
                                <Badge 
                                  key={moduleTag} 
                                  variant="outline" 
                                  className="text-xs capitalize"
                                >
                                  {moduleTag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}